import { useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { Rock, User, RockCategory } from '../../types'
import RockRow from './RockRow'
import UserAvatar from '../shared/UserAvatar'
import { sortByPerson } from '../../utils/peopleSort'

interface Props {
  rocks: Rock[]
  teamId: number
  groupBy: string
  isBacklog?: boolean
  teamMembers: User[]
  categories: RockCategory[]
  onViewDetail: (rock: Rock) => void
  canReorder?: boolean
  /**
   * Where a drag-reorder is saved. Rock Review keeps its own per-meeting
   * order, so it points this at the meeting's endpoint instead of the team's.
   */
  reorderUrl?: string
  /** Links check-ins recorded from a row to the meeting they happened in. */
  meetingId?: number
  /** Shows the latest check-in note under each rock title (F.A.S.T. Rocks and Rock Review both do). */
  showCheckInNote?: boolean
  /**
   * Task #798: renders the per-row actions menu ("Create Topic") to the right
   * of "Check in". Opt-in so only the in-meeting Rock Review table gets it —
   * the standalone Rocks page and the backlog view stay unchanged.
   */
  showRowMenu?: boolean
  onCreateTopic?: (rock: Rock) => void
}

interface GroupedRocks {
  key: string
  label: string
  owner?: User
  rocks: Rock[]
}

function groupRocks(rocks: Rock[], groupBy: string, _teamMembers: User[], categories: RockCategory[]): GroupedRocks[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', rocks }]
  }

  if (groupBy === 'category') {
    const catMap = new Map<string, { label: string; rocks: Rock[] }>()
    const uncategorized: Rock[] = []

    categories.forEach(c => {
      catMap.set(String(c.id), { label: c.name, rocks: [] })
    })

    rocks.forEach(r => {
      if (r.category_id && catMap.has(String(r.category_id))) {
        catMap.get(String(r.category_id))!.rocks.push(r)
      } else {
        uncategorized.push(r)
      }
    })

    const groups: GroupedRocks[] = []
    catMap.forEach((val, key) => {
      if (val.rocks.length > 0) {
        groups.push({ key, label: val.label, rocks: val.rocks })
      }
    })
    if (uncategorized.length > 0) {
      groups.push({ key: 'uncategorized', label: 'Uncategorized', rocks: uncategorized })
    }
    return groups
  }

  const ownerMap = new Map<number, { owner: User; rocks: Rock[] }>()
  rocks.forEach(r => {
    if (!ownerMap.has(r.owner_id)) {
      ownerMap.set(r.owner_id, { owner: r.owner, rocks: [] })
    }
    ownerMap.get(r.owner_id)!.rocks.push(r)
  })

  // Task #613: owner groups read alphabetically via the shared comparator.
  return sortByPerson(
    Array.from(ownerMap.entries()).map(([id, { owner, rocks: ownerRocks }]) => ({
      key: String(id),
      label: owner.full_name,
      owner,
      rocks: ownerRocks,
    })),
    g => g.owner
  )
}

function tableHeader(isBacklog: boolean | undefined, dragColumn: boolean, showOwner: boolean) {
  return (
    <thead>
      <tr className="border-b border-stone/20">
        {dragColumn && <th className="w-8" />}
        <th className="text-left py-2 px-4 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading">Rock Name</th>
        {showOwner && (
          <th className="text-left py-2 px-3 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading w-44">Owner</th>
        )}
        <th className="text-center py-2 px-3 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading w-20">Miles.</th>
        <th className="text-left py-2 px-3 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading w-24">Due</th>
        {!isBacklog && (
          <>
            <th className="text-left py-2 px-3 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading w-56">% Complete</th>
            <th className="text-left py-2 px-3 text-sm font-bold text-text-secondary uppercase tracking-wider font-heading w-44 min-w-[11rem]">Last Checked In</th>
          </>
        )}
      </tr>
    </thead>
  )
}

export default function RockTable({
  rocks, teamId, groupBy, isBacklog, teamMembers, categories, onViewDetail, canReorder,
  reorderUrl, meetingId, showCheckInNote, showRowMenu, onCreateTopic,
}: Props) {
  const groups = groupRocks(rocks, groupBy, teamMembers, categories)
  const dragEnabled = !!canReorder && groupBy === 'none' && !isBacklog
  const showOwner = groupBy !== 'owner'
  const [orderedRocks, setOrderedRocks] = useState<Rock[]>(rocks)
  // Row actions menu open state lives here, above the rows, so opening one
  // row's menu closes any other (task #798). The backlog view has no status
  // cell to hang it off, so it never gets the menu.
  const [openMenuRockId, setOpenMenuRockId] = useState<number | null>(null)
  const rowMenuEnabled = showRowMenu === true && !isBacklog
  const rowMenuProps = (rock: Rock) => (rowMenuEnabled ? {
    showRowMenu: true,
    menuOpen: openMenuRockId === rock.id,
    onToggleMenu: () => setOpenMenuRockId(prev => (prev === rock.id ? null : rock.id)),
    onCloseMenu: () => setOpenMenuRockId(prev => (prev === rock.id ? null : prev)),
    onCreateTopic,
  } : {})
  // A reorder is optimistic until its PUT lands. The meeting page re-serializes
  // every prop on its 10s heartbeat, and an in-flight heartbeat can carry the
  // pre-drop order — adopting it would snap the list back under the user.
  const reorderPendingRef = useRef(false)

  useEffect(() => {
    if (reorderPendingRef.current) return
    setOrderedRocks(rocks)
  }, [rocks])

  if (rocks.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto w-12 h-12 text-text-primary/20 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-text-muted text-sm">
          {isBacklog ? 'No rocks in backlog' : 'No rocks found'}
        </p>
      </div>
    )
  }

  if (dragEnabled) {
    const handleDragEnd = (result: DropResult) => {
      if (!result.destination || result.destination.index === result.source.index) return
      const next = Array.from(orderedRocks)
      const [moved] = next.splice(result.source.index, 1)
      next.splice(result.destination.index, 0, moved)
      setOrderedRocks(next)

      reorderPendingRef.current = true
      router.put(reorderUrl || `/teams/${teamId}/rocks/reorder`, {
        rock_ids: next.map(r => r.id)
      }, {
        preserveScroll: true,
        preserveState: true,
        only: [],
        onFinish: () => { reorderPendingRef.current = false },
      })
    }

    return (
      <div className="bg-white rounded-xl shadow-card overflow-x-auto">
        <table className="w-full">
          {tableHeader(isBacklog, true, showOwner)}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="rocks-list">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {orderedRocks.map((rock, index) => (
                    <Draggable key={rock.id} draggableId={String(rock.id)} index={index}>
                      {(prov, snapshot) => (
                        <RockRow
                          ref={prov.innerRef}
                          rock={rock}
                          teamId={teamId}
                          isBacklog={isBacklog}
                          showOwner={showOwner}
                          onViewDetail={onViewDetail}
                          dragHandleProps={prov.dragHandleProps}
                          rowProps={prov.draggableProps}
                          isDragging={snapshot.isDragging}
                          meetingId={meetingId}
                          showCheckInNote={showCheckInNote}
                          {...rowMenuProps(rock)}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {groups.map(group => (
        <div key={group.key}>
          {group.label && (
            <div className="flex items-center gap-2 mb-2 px-4">
              {group.owner && <UserAvatar user={group.owner} size="sm" />}
              <h3 className="text-base font-semibold text-text-primary font-heading">{group.label}</h3>
              <span className="text-sm text-text-muted">({group.rocks.length})</span>
            </div>
          )}
          <div className="bg-white rounded-xl shadow-card overflow-x-auto">
            <table className="w-full" style={{ userSelect: 'auto', WebkitUserDrag: 'none' } as React.CSSProperties}>
              {tableHeader(isBacklog, false, showOwner)}
              <tbody>
                {group.rocks.map(rock => (
                  <RockRow
                    key={rock.id}
                    rock={rock}
                    teamId={teamId}
                    isBacklog={isBacklog}
                    showOwner={showOwner}
                    onViewDetail={onViewDetail}
                    meetingId={meetingId}
                    showCheckInNote={showCheckInNote}
                    {...rowMenuProps(rock)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
